import i18n from "roddeh-i18n";
import { loadStripe, StripeCardElement } from "@stripe/stripe-js";
import moment from "moment";

import { PaymentMethod } from "../generated/graphql";
import en from "../translations/en.json";
import es from "../translations/es.json";

moment.updateLocale("es", {
  months: [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ],
  weekdays: [
    "Domingo",
    "Lunes",
    "Martes",
    "Miércoles",
    "Jueves",
    "Viernes",
    "Sabado",
  ],
});

export const stripePromise = loadStripe(process.env.STRIPE_API_KEY);

export const tokenizeBankAccount = async (
  routingNumber: string,
  accountNumber: string,
  fullName: string
): Promise<{ errors: any; token: string }> => {
  const response = { errors: null, token: null };

  const stripe = await stripePromise;

  await stripe
    .createToken("bank_account", {
      country: "US",
      currency: "usd",
      routing_number: routingNumber,
      account_number: accountNumber,
      account_holder_name: fullName,
      account_holder_type: "individual",
    })
    .then(function(result) {
      if (result.error) {
        response.errors = result.error;
      }

      if (result.token && result.token.bank_account) {
        response.token = result.token.id;
      }
    });

  return response;
};

export const tokenizeCard = async (
  cardElement: StripeCardElement,
  data?: { name: string; currency: string }
): Promise<{ errors: any; token: string }> => {
  const response = { errors: null, token: null };
  const stripe = await stripePromise;

  await stripe.createToken(cardElement, data).then(function(result) {
    if (result.error) {
      response.errors = result.error;
    }

    if (result.token && result.token.card) {
      response.token = result.token.id;
    }
  });

  return response;
};

export const states = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District Of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  { value: "AS", label: "American Samoa" },
  { value: "GU", label: "Guam" },
  { value: "MP", label: "Northern Mariana Islands" },
  { value: "PR", label: "Puerto Rico" },
  { value: "UM", label: "United States Minor Outlying Islands" },
  { value: "VI", label: "Virgin Islands" },
];

export const formatPhoneNumber = (str: string): string => {
  const x = str.replace(/\D/g, "").match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  str = !x[2] ? x[1] : "(" + x[1] + ") " + x[2] + (x[3] ? "-" + x[3] : "");

  return str;
};

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  // These options are needed to round to whole numbers if that's what you want.
  // minimumFractionDigits: 0,
  // maximumFractionDigits: 0,
});

export const formatAmount = (amount: number): string => {
  return formatter.format(amount);
};

export const paymentMethodLabel = (paymentMethod: PaymentMethod): string => {
  if (paymentMethod.type == "bank_account") {
    return `${paymentMethod.bankName}...${paymentMethod.last4}`;
  } else {
    return `${paymentMethod.brand}...${paymentMethod.last4}`;
  }
};

const translations = [
  { lang: "en", values: en },
  { lang: "es", values: es },
];

export class TranslationManager {
  private static myInstance = null;
  private translations: Map<string, (phrase: string) => {}>;
  private language: string;

  constructor() {
    this.translations = new Map();
    this.language = "en";

    translations.forEach(element => {
      if (!this.translations.has(element.lang)) {
        this.translations.set(
          element.lang,
          i18n.create({
            values: element.values,
          })
        );
      }
    });
  }

  public setLanguage(lang: string): void {
    this.language = lang;
  }

  public translate(phrase: string) {
    return this.translations.has(this.language)
      ? this.translations.get(this.language)(phrase)
      : this.translations.get("en")(phrase);
  }

  public static getInstance(): TranslationManager {
    if (TranslationManager.myInstance == null) {
      TranslationManager.myInstance = new TranslationManager();
    }

    return this.myInstance;
  }
}

export const translate = (phrase: string, lang?: string): string => {
  const t = TranslationManager.getInstance();

  if (lang) {
    t.setLanguage(lang);
  }

  const tPhrase = t.translate(phrase);
  return `${tPhrase}`;
};

export const setLanguage = (lang: string) => {
  const t = TranslationManager.getInstance();

  t.setLanguage(lang);
};

export const isPendingBankAccount = (paymentMethod: PaymentMethod): boolean =>
  paymentMethod.type === "bank_account" && paymentMethod.status !== "verified";

export const formatDate = (date: any, language: string): string => {
  // console.log(moment.locales());
  const local = moment(date).locale(language);
  // return localLocale.format("MMMM Do, YYYY")
  // return local.format("LLLL");
  return local.format("MMMM Do, YYYY");
};

export const getDate = (date: any, language: string): any => {
  return moment(date).locale(language);
};
